import Phaser from "phaser";
import { installLetterboxFit } from "../ui/responsive";
import { loadSettings } from "../../ai/localMemory";
import { t, type Language } from "../../i18n";
import { dailyBoardKey, ENDLESS_BOARD, fetchTop } from "../../net/leaderboard";
import { dateKeyOf } from "../dailyChallenge";
import mapConfig from "../data/mapConfig.json";

/** Global leaderboard view (today's Daily Challenge + Endless survival). Fails soft: if the
 * backend store is unconfigured or unreachable, each column shows an empty state. */
export class LeaderboardScene extends Phaser.Scene {
  private language: Language = "ko";

  constructor() {
    super("LeaderboardScene");
  }

  create() {
    installLetterboxFit(this);
    this.language = loadSettings().language;
    this.add.image(640, 360, mapConfig.mapAssetKey).setDisplaySize(1280, 720);
    this.add.rectangle(640, 360, 1280, 720, 0x08070a, 0.62);
    this.add.rectangle(640, 360, 900, 560, 0x11131b, 0.94).setStrokeStyle(2, 0xc9b878, 0.9);
    this.add.text(640, 100, t(this.language, "leaderboard"), style("#ffe58e", 28)).setOrigin(0.5);

    const today = dateKeyOf(new Date());
    this.drawColumn(380, t(this.language, "leaderboardDaily"), dailyBoardKey(today), "");
    this.drawColumn(900, t(this.language, "leaderboardEndless"), ENDLESS_BOARD, "s");

    this.createButton(640, 648, t(this.language, "backToMenu"), () => this.scene.start("MenuScene"));
    this.input.keyboard?.on("keydown-ESC", () => this.scene.start("MenuScene"));
  }

  private drawColumn(cx: number, heading: string, board: string, suffix: string) {
    this.add.text(cx, 156, heading, style("#9be8ff", 20)).setOrigin(0.5);
    const loading = this.add.text(cx, 208, t(this.language, "leaderboardLoading"), style("#b9b1a0", 15)).setOrigin(0.5);
    void fetchTop(board, 10).then((entries) => {
      if (!this.scene.isActive()) return;
      loading.destroy();
      if (entries.length === 0) {
        this.add
          .text(cx, 210, t(this.language, "leaderboardEmpty"), { ...style("#8d8678", 14), wordWrap: { width: 320 }, align: "center" })
          .setOrigin(0.5, 0);
        return;
      }
      entries.forEach((entry, index) => {
        const y = 198 + index * 33;
        const color = index === 0 ? "#ffe58e" : index === 1 ? "#dfe9ff" : index === 2 ? "#e6c9a8" : "#cfc8b8";
        this.add.text(cx - 175, y, `${index + 1}`, style(color, 15)).setOrigin(0, 0.5);
        this.add.text(cx - 145, y, entry.name.slice(0, 14), style(color, 15)).setOrigin(0, 0.5);
        this.add.text(cx + 175, y, `${entry.score}${suffix}`, style(color, 15)).setOrigin(1, 0.5);
      });
    });
  }

  private createButton(x: number, y: number, label: string, onClick: () => void) {
    const button = this.add.container(x, y).setDepth(200);
    const frame = this.add.rectangle(0, 0, 150, 40, 0x17364a, 0.96).setStrokeStyle(1, 0xc9b878, 0.9);
    const text = this.add.text(0, 0, label, style("#f7f2df", 15)).setOrigin(0.5);
    button.add([frame, text]);
    button.setSize(150, 40).setInteractive({ useHandCursor: true });
    button.on("pointerover", () => frame.setFillStyle(0x235372, 1));
    button.on("pointerout", () => frame.setFillStyle(0x17364a, 0.96));
    button.on("pointerdown", onClick);
  }
}

function style(color: string, fontSize: number): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
    fontSize: `${fontSize}px`,
    color,
    stroke: "#08070a",
    strokeThickness: 3
  };
}
